<template>
  <div class="wrapper w-100">
    <survey-question-text-field
      v-if="preview && !isNew"
      :question="value"
    ></survey-question-text-field>
    <div class="clickLayer text-right rounded" @click.stop="edit">
      <div
        v-if="preview && !isNew"
        class="mt-3 mr-2 primary rounded elevation-3 overflow-hidden d-inline-block"
      >
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              class="white--text"
              tile
              icon
              @click.stop="edit"
            >
              <v-icon small>$edit</v-icon>
            </v-btn>
          </template>
          <span>Edit Question</span>
        </v-tooltip>
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              class="white--text"
              tile
              icon
              @click.stop="$emit('delete')"
            >
              <v-icon small>$delete</v-icon>
            </v-btn>
          </template>
          <span>Delete Question</span>
        </v-tooltip>
      </div>
      <div
        class="mt-3 mr-3 primary rounded elevation-3 overflow-hidden d-inline-block"
      >
        <v-tooltip top v-if="questionIndex !== 0">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              class="white--text"
              tile
              icon
              @click.stop="$emit('move', -1)"
            >
              <v-icon small>$sortUp</v-icon>
            </v-btn>
          </template>
          <span>Move Up</span>
        </v-tooltip>
        <v-tooltip top v-if="questionIndex !== page.questions.length - 1">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              class="white--text"
              tile
              icon
              @click.stop="$emit('move', 1)"
            >
              <v-icon small>$sortDown</v-icon>
            </v-btn>
          </template>
          <span>Move Down</span>
        </v-tooltip>
      </div>
    </div>

    <v-dialog
      v-model="openEditor"
      scrollable
      fullscreen
      hide-overlay
      persistent
      no-click-animation
      transition="dialog-bottom-transition"
    >
      <ValidationObserver ref="observer" tag="form" @submit.prevent="onSubmit">
        <v-card rounded="0">
          <div class="elevation-3 primary">
            <v-container class="pa-0">
              <dialog-title
                :title="`${value.key === '' ? 'Add' : 'Edit'} Question`"
                class="white--text pl-md-0"
                @close="onCancel"
              >
                <template v-slot:actions="{ close }">
                  <v-btn dark icon @click="close">
                    <v-icon>$close</v-icon></v-btn
                  >
                </template>
              </dialog-title>
            </v-container>
          </div>
          <v-card-text ref="body">
            <v-container>
              <v-row>
                <v-col cols="12">
                  Fill out the form below to change the question.
                </v-col>
                <v-col cols="12">
                  <survey-editor-page-select
                    v-model="innerPageKey"
                    :pages="pages"
                    :locked="locked"
                  ></survey-editor-page-select>
                </v-col>
                <v-col cols="4">
                  <ValidationProvider
                    :rules="{
                      required: true,
                      min: 2,
                      max: 100,
                      notTaken: questionKeys.filter((x) => x !== value.key),
                    }"
                    v-slot="{ errors }"
                    vid="key"
                  >
                    <v-text-field
                      label="Key*"
                      v-model="innerValue.key"
                      outlined
                      :error-messages="errors"
                      persistent-hint
                      hint="Cannot be changed once added."
                      :disabled="!isNew || locked"
                      :autofocus="isNew"
                    ></v-text-field>
                  </ValidationProvider>
                </v-col>

                <v-col cols="4">
                  <ValidationProvider
                    :rules="{
                      required: true,
                      min: 2,
                      max: 100,
                      notTaken: questionMappingKeys.filter(
                        (x) => x !== value.mappingKey
                      ),
                    }"
                    v-slot="{ errors }"
                    vid="mappingKey"
                  >
                    <v-text-field
                      label="Mapping Key*"
                      v-model="innerValue.mappingKey"
                      outlined
                      :error-messages="errors"
                      persistent-hint
                      hint=""
                      :autofocus="isNew"
                      :disabled="locked"
                    ></v-text-field>
                  </ValidationProvider>
                </v-col>

                <v-col cols="4">
                  <ValidationProvider
                    rules="required|min:2|max:500"
                    v-slot="{ errors }"
                    vid="Type"
                  >
                    <v-select
                      label="Type*"
                      v-model="innerValue.type"
                      :items="types"
                      outlined
                      :error-messages="errors"
                      disabled
                      rows="3"
                    ></v-select>
                  </ValidationProvider>
                </v-col>

                <v-col cols="12" xl="1" lg="2" md="3">
                  <ValidationProvider
                    rules="max:200"
                    v-slot="{ errors }"
                    vid="Text"
                  >
                    <v-text-field
                      label="Number"
                      v-model="innerValue.questionNumber"
                      outlined
                      :error-messages="errors"
                      :disabled="locked"
                    ></v-text-field>
                  </ValidationProvider>
                </v-col>

                <v-col cols="12" xl="11" lg="10" md="9">
                  <ValidationProvider
                    rules="required|min:2|max:2000"
                    v-slot="{ errors }"
                    vid="Text"
                  >
                    <v-textarea
                      label="Question*"
                      v-model="innerValue.text"
                      outlined
                      :error-messages="errors"
                      rows="3"
                      :disabled="locked"
                    ></v-textarea>
                  </ValidationProvider>
                </v-col>
                <v-col cols="12" md="4" sm="6" class="pt-6">
                  <ValidationProvider
                    rules="required|numeric|min_value:0|max_value:4000"
                    v-slot="{ errors }"
                    vid="settingsMinLength"
                  >
                    <v-text-field
                      label="Minimum Length*"
                      v-model="
                        innerValue.settings.find(
                          (x) => x.key == questionSettings.minLength.key
                        ).value
                      "
                      outlined
                      class="align-input-right"
                      :error-messages="errors"
                      persistent-hint
                      hint="The minimum number of characters in an answer."
                      :disabled="locked"
                    >
                    </v-text-field>
                  </ValidationProvider>
                </v-col>
                <v-col cols="12" md="4" sm="6" class="pt-6">
                  <ValidationProvider
                    rules="required|numeric|min_value:2|max_value:4000"
                    v-slot="{ errors }"
                    vid="settingsMaxLength"
                  >
                    <v-text-field
                      label="Maximum Length*"
                      v-model="
                        innerValue.settings.find(
                          (x) => x.key == questionSettings.maxLength.key
                        ).value
                      "
                      outlined
                      class="align-input-right"
                      :error-messages="errors"
                      persistent-hint
                      hint="The maximum number of characters in an answer."
                      :disabled="locked"
                    >
                    </v-text-field>
                  </ValidationProvider>
                </v-col>
                
                <v-col md="4" sm="6" class="pt-6">
                  <ValidationProvider
                    v-slot="{ errors }"
                    vid="ResponseCountTarget"
                  >
                    <v-text-field
                      type="number"
                      v-model="innerValue.responseCountTarget"
                      label="Response Goal"
                      hint="Used to calculate a question's response rate goals. Only needed if the goal is different than the base survey response goal."
                      persistent-hint
                      outlined
                      :error-messages="errors"
                      :disabled="locked || optionGoals"
                    ></v-text-field>
                  </ValidationProvider>
                </v-col>
                <v-col cols="12">
                  <div class="d-flex align-center">
                    <v-btn
                      icon
                      :color="required ? 'secondary' : ''"
                      @click="required = !required"
                      :disabled="locked"
                    >
                      <v-icon>
                        {{ required ? "$checked" : "$unchecked" }}
                      </v-icon>
                    </v-btn>
                    <div>Required</div>
                  </div>
                  <div class="d-flex align-center">
                    <v-btn icon :color="required ? 'secondary' : ''" disabled>
                      <v-icon>
                        {{ isPrivate ? "$checked" : "$unchecked" }}
                      </v-icon>
                    </v-btn>
                    <div>Hide on Public Reporting</div>
                  </div>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-divider class="mb-3"></v-divider>
                  <div class="text-h6">Conditional Rules</div>
                  <p class="text-body-2">
                    Use the section below to create conditional rules for your
                    question.
                  </p>

                  <question-rules
                    v-for="rule in rules"
                    :key="rule.key"
                    :label="rule.label"
                    :ruleKey="rule.key"
                    :queryRules="queryRules"
                    :locked="locked"
                    v-model="innerValue.rules"
                    class="mb-4"
                  ></question-rules>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions class="px-4 py-0 elevation-3">
            <v-container>
              <div class="d-flex align-center">
                <small class="flex-fill">*indicates required field</small>
                <v-btn text color="primary" @click="onCancel"> Cancel </v-btn>
                <v-btn v-if="!locked" type="submit" color="primary">
                  Ok
                </v-btn>
              </div>
            </v-container>
          </v-card-actions>
        </v-card>
      </ValidationObserver>
    </v-dialog>
  </div>
</template>

<style scoped lang="scss">
.wrapper {
  position: relative;
  z-index: 0;

  .clickLayer {
    opacity: 0;
    position: absolute;
    z-index: 1;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.15);
    transition: opacity 300ms ease-in-out;
    cursor: pointer;

    &:hover {
      opacity: 1;
    }
  }
}
</style>

<script>
import DialogTitle from "./DialogTitle.vue";
import SurveyQuestionTextField from "./Survey.Question.TextField.vue";
import {
  questionTypeOptions,
  questionSettings,
  getQuestionSetting,
  ruleTypes,
} from "../constants/Survey";
import equal from "deep-is";
import SurveyEditorPageSelect from "./Survey.Editor.PageSelect.vue";
import QuestionRules from "./QuestionRules.vue";

const newOption = () => ({
  text: "",
  value: "",
  responseCountTarget: null,
  weight: null,
});

export default {
  name: "SurveyQuestionTextFieldEditor",
  components: {
    DialogTitle,
    SurveyQuestionTextField,
    SurveyEditorPageSelect,
    QuestionRules,
  },
  data: () => ({
    innerValue: {
      settings: [],
      options: [],
    },
    innerPageKey: "",
    openEditor: false,
    questionSettings,
  }),
  props: {
    preview: {
      type: Boolean,
      default: true,
    },
    value: {
      type: Object,
    },
    questionIndex: {
      type: Number,
      required: true,
    },
    pages: {
      type: Array,
      required: true,
    },
    pageKey: {
      type: String,
      required: true,
    },
    questionKeys: {
      type: Array,
      required: true,
    },
    questionMappingKeys: {
      type: Array,
      required: true,
    },
    queryRules: {
      type: Array,
      required: true,
    },
    locked: Boolean,
  },
  computed: {
    types() {
      return questionTypeOptions;
    },
    pageOptions() {
      return this.pages.map((x) => {
        return {
          text: x.displayName,
          value: x.key,
        };
      });
    },
    page() {
      return this.pages.find((x) => x.key === this.pageKey);
    },
    isNew() {
      return this.value.key === "";
    },
    rules() {
      let rules = [];

      if (!this.required)
        rules.push({
          label: "Required If",
          key: ruleTypes.requiredIf,
        });

      rules.push({
        label: "Optional If",
        key: ruleTypes.optionalIf,
      });

      rules.push({
        label: "Show If",
        key: ruleTypes.showIf,
      });

      rules.push({
        label: "Hide If",
        key: ruleTypes.hideIf,
      });

      return rules;
    },
    required: {
      get() {
        return (
          getQuestionSetting(
            this.innerValue.settings,
            questionSettings.required.key
          ).value == "true"
        );
      },
      set(val) {
        this.innerValue.settings.find(
          (x) => x.key === questionSettings.required.key
        ).value = val + "";
      },
    },
    isPrivate: {
      get() {
        return (
          getQuestionSetting(
            this.innerValue.settings,
            questionSettings.private.key
          ).value == "true"
        );
      },
      set(val) {
        this.innerValue.settings.find(
          (x) => x.key === questionSettings.private.key
        ).value = val + "";
      },
    },
  },
  watch: {
    value(val) {
      if (!equal(val, this.innerValue)) this.applyDefaults();
    },
    pageKey(value) {
      if (value != this.innerPageKey)
        this.innerPageKey = JSON.parse(JSON.stringify(value));
    },
    openEditor(val) {
      if (val) {
        this.innerValue = JSON.parse(JSON.stringify(this.value));

        this.addingOption = false;
        this.addOption = newOption();
        this.editingOption = -1;
        this.editOption = newOption();
        this.deletingOption = -1;

        if (this.pageKey != this.innerPageKey)
          this.innerPageKey = JSON.parse(JSON.stringify(this.pageKey));

        this.optionGoals = this.hasOptionGoals ? true : false;

        if (this.$refs.body) {
          setTimeout(() => (this.$refs.body.scrollTop = 0), 1);
        }
      }
    },
  },
  methods: {
    edit() {
      if (this.$refs.observer) this.$refs.observer.reset();
      this.openEditor = true;
    },
    onSubmit() {
      this.$nextTick(() => {
        this.$refs.observer.validate().then((isValid) => {
          if (isValid) {
            if (this.innerValue.responseCountTarget === "") {
              this.innerValue.responseCountTarget = null;
            }

            this.$emit("input", this.innerValue);
            if (this.pageKey != this.innerPageKey) {
              this.$emit("set-page", this.innerPageKey);
            }
            this.openEditor = false;
          }
        });
      });
    },
    applyDefaults() {
      if (this.value) {
        let mirror = JSON.parse(JSON.stringify(this.value));
        let changed = false;

        let defaultSettings = [
          {
            key: questionSettings.required.key,
            value: questionSettings.required.default,
          },
          {
            key: questionSettings.private.key,
            value: "true",
          },
          {
            key: questionSettings.minLength.key,
            value: questionSettings.minLength.default,
          },
          {
            key: questionSettings.maxLength.key,
            value: questionSettings.maxLength.default,
          },
        ];

        if (!mirror.settings) {
          mirror.settings = [];
          changed = true;
        }

        for (let i = 0; i < defaultSettings.length; i++) {
          if (!mirror.settings.some((x) => x.key === defaultSettings[i].key)) {
            mirror.settings.push(defaultSettings[i]);
            changed = true;
          }
        }

        if (changed) this.$emit("input", mirror);
        this.innerValue = JSON.parse(JSON.stringify(mirror));
      }
    },
    onCancel() {
      if (this.isNew) this.$emit("force-delete");
      this.openEditor = false;
    },
  },
  mounted() {
    if (this.pageKey)
      this.innerPageKey = JSON.parse(JSON.stringify(this.pageKey));
    this.applyDefaults();
  },
};
</script>
